// Pièces du candidat téléversées (CV, portfolio, lettre) — carte
// « Documents » du panneau candidat. Le contenu est servi par
// GET /applications/:id/files/:fileId (portée de la candidature) ; la
// liste ne porte que nom, type et taille. Un clic ouvre la pièce dans
// le lecteur intégré (FileViewer), sans quitter la plateforme.
import { useRef, useState } from 'react';
import { toast } from '../../store/toast.js';
import {
  recFileUrl, useRecFileDelete, useRecFileUpload,
  type RecApplicationDTO, type RecFileKind,
} from '../../api/hooks.js';
import { FileViewer, type ViewerDoc } from './FileViewer.js';

const KIND_LBL: Record<RecFileKind, string> = { cv: 'CV', portfolio: 'Portfolio', lettre: 'Lettre', autre: 'Autre' };
const MAX = 8 * 1024 * 1024;
const fmtSize = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} Ko` : `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`);

export function CandidateFiles({ app, canEdit, dark }: { app: RecApplicationDTO; canEdit: boolean; dark: boolean }) {
  const files = app.candidate.files ?? [];
  const upload = useRecFileUpload();
  const del = useRecFileDelete();
  const input = useRef<HTMLInputElement>(null);
  const [view, setView] = useState<ViewerDoc | null>(null);
  const [kind, setKind] = useState<RecFileKind>(files.some((f) => f.kind === 'cv') ? 'portfolio' : 'cv');

  const onPick = (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX) { toast.danger('Fichier trop lourd — 8 Mo maximum.'); return; }
    upload.mutate({ appId: app.id, file: f, kind }, {
      onSuccess: () => toast.success(`${KIND_LBL[kind]} ajouté — ${f.name}`),
      onError: (e) => toast.danger((e as { body?: { message?: string; error?: string } }).body?.message
        ?? ((e as { body?: { error?: string } }).body?.error === 'file_too_large' ? 'Fichier trop lourd — 8 Mo maximum.' : 'Téléversement impossible.')),
    });
    if (input.current) input.current.value = '';
  };

  return (
    <div className="rec-files" data-testid="rec-files">
      {files.map((f) => (
        <div key={f.id} className="rec-file">
          <a href={recFileUrl(app.id, f.id)} target="_blank" rel="noreferrer" title={`Ajouté par ${f.by ?? '—'}`}
            onClick={(e) => { e.preventDefault(); setView({ title: `${KIND_LBL[f.kind] ?? f.kind} — ${f.filename}`, url: recFileUrl(app.id, f.id), mime: f.mime }); }}
            data-testid={`rec-file-${f.id}`}>
            <span className="k">{KIND_LBL[f.kind] ?? f.kind}</span>
            <span className="n">{f.filename}</span>
            <span className="s">{fmtSize(f.size)}</span>
          </a>
          {canEdit && (
            <button className="x" aria-label={`Supprimer ${f.filename}`} disabled={del.isPending}
              onClick={() => { if (window.confirm(`Supprimer « ${f.filename} » ?`)) del.mutate({ appId: app.id, fileId: f.id }); }}>✕</button>
          )}
        </div>
      ))}
      {view && <FileViewer doc={view} dark={dark} onClose={() => setView(null)} />}
      {canEdit && (
        <div className="rec-fileadd">
          <select value={kind} onChange={(e) => setKind(e.target.value as RecFileKind)} aria-label="Type de pièce">
            {(Object.keys(KIND_LBL) as RecFileKind[]).map((k) => <option key={k} value={k}>{KIND_LBL[k]}</option>)}
          </select>
          <button className="rec-ghost sm" disabled={upload.isPending} onClick={() => input.current?.click()} data-testid="rec-file-add">
            {upload.isPending ? 'Envoi…' : '＋ Téléverser'}
          </button>
          <input ref={input} type="file" hidden accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf"
            onChange={(e) => onPick(e.target.files?.[0])} data-testid="rec-file-input" />
        </div>
      )}
    </div>
  );
}
